import { PaymentMethodType } from './payment-types.types';
import { EntityTypes, Timestamps, Versioned } from './shared.types';

export enum ShiftCommandType {
  OPEN_SHIFT = 'OPEN_SHIFT',
  ADD_TRANSACTION = 'ADD_TRANSACTION',
  START_CLOSE = 'START_CLOSE',
  FINALIZE_CLOSE = 'FINALIZE_CLOSE',
  INSTANT_CLOSE = 'INSTANT_CLOSE',
  ADD_COMMENT = 'ADD_COMMENT',
  RECORD_CLOSE_AUDIT = 'RECORD_CLOSE_AUDIT',
}

/**
 * Stages tracked in the closing audit trail. Each step transition in the
 * CloseShiftStepper records one entry, plus a final FINALIZED entry written
 * by the instant-close use-case.
 */
export enum CloseShiftStepEvent {
  BALANCES_SUBMITTED = 'BALANCES_SUBMITTED',
  RECONCILED = 'RECONCILED',
  FINALIZED = 'FINALIZED',
}

/**
 * Enum representing shift statuses
 */
export enum ShiftStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  PENDING_CLOSE = 'PENDING_CLOSE',
  NEEDS_ATTENTION = 'NEEDS_ATTENTION',
  CLOSED_WITH_VARIANCE = 'CLOSED_WITH_VARIANCE',
}

/**
 * Enum representing reconciliation result per payment method
 */
export enum ReconciliationStatus {
  BALANCED = 'BALANCED',
  SHORT = 'SHORT',
  OVER = 'OVER',
  UNRECONCILED = 'UNRECONCILED',
}

/**
 * Base shift interface
 */
export interface Shift extends Timestamps, Versioned {
  id: string;
  pos_id?: string | null | undefined;
  status: ShiftStatus;

  // Opening details
  opened_at: string;
  opened_by: string;
  opened_by_name: string;
  opened_user_type: EntityTypes;
  opening_note?: string | null | undefined;

  // Closing details
  /**
   * Set the FIRST time an operator submits closing balances from the
   * CloseShiftStepper (step 1 → Reconcile). Never overwritten.
   * Together with `closed_at`, this brackets the "during close" session
   * window; transactions created/reversed in this interval are the
   * forgotten-transactions the operator reconciled against while closing.
   */
  closing_started_at?: string | null | undefined;
  closed_at?: string | null | undefined;
  closed_by?: string | null | undefined;
  closed_by_name?: string | null | undefined;
  closed_user_type?: EntityTypes;
  closing_note?: string | null | undefined;

  // Multi-payment balances
  opening_balances: ShiftReportPaymentMethod[];
  closing_balances?: ShiftReportPaymentMethodWithReconciliation[];

  // Metadata
  merchant_id: string;
  store_id: string;
  comments: ShiftComment[];

  closing_attempts: (CloseShiftAttempt | CloseShiftWithReconciliation)[];

  /**
   * Append-only audit trail of every step the operator passed through in the
   * close-shift stepper, including the final FINALIZED entry written when the
   * instant-close API is invoked. Captures what the operator entered, what
   * the server expected at that moment, the per-method differences, and the
   * ids of any transactions created or reversed during the closing session.
   */
  closing_audit_trail: ClosingAuditTrailEntry[];
}

export interface ClosingAuditTrailActor {
  id: string;
  name: string;
  type: EntityTypes;
}

export interface ClosingAuditTrailExpectedBalance {
  payment_method_id: string;
  payment_method_name: string;
  payment_method_type: PaymentMethodType;
  expected_amount: number;
}

export interface ClosingAuditTrailDifference {
  payment_method_id: string;
  difference: number;
  reconciliation_status: ReconciliationStatus;
}

export interface ClosingAuditTrailEntry extends Timestamps {
  id: string;
  event: CloseShiftStepEvent;
  recorded_at: string;
  actor: ClosingAuditTrailActor;
  /** Balances the operator typed at this step. */
  entered_balances: PaymentBalance[];
  /** Server-computed expected balance per payment method at the moment. */
  expected_balances: ClosingAuditTrailExpectedBalance[];
  /** Cached differences (actual - expected) and per-method reconciliation status. */
  differences: ClosingAuditTrailDifference[];
  /**
   * Account-transaction ids created or reversed since `closing_started_at`.
   * Empty on the very first BALANCES_SUBMITTED event (when
   * `closing_started_at` is being stamped).
   */
  session_transaction_ids?: string[] | null | undefined;
  client_step_index: number;
  note?: string | null | undefined;
}

/**
 * Request body for POST /shifts/{merchant_id}/{store_id}/{shift_id}/close/audit.
 * Server fills in `id`, `recorded_at`, `expected_balances`, `differences`, and
 * resolves `session_transaction_ids` from `closing_started_at`.
 */
export interface RecordCloseAuditEntry {
  event: CloseShiftStepEvent;
  actor: ClosingAuditTrailActor;
  entered_balances: PaymentBalance[];
  client_step_index: number;
  note?: string | null | undefined;
}

export interface CloseShiftAttempt extends AttemptToCloseShift, Timestamps {}

export interface ShiftReportPaymentMethod extends Timestamps {
  payment_method_id: string;
  payment_method_name: string;
  payment_method_type: PaymentMethodType;
  opening_balance: number;
  note?: string | null | undefined;
}

export interface ShiftComment extends Timestamps {
  id: string;
  shift_id: string;
  user_id: string;
  user_name: string;
  user_type: EntityTypes;
  comment: string;
}

export interface ShiftReportPaymentMethodWithReconciliation
  extends ShiftReportPaymentMethod {
  reconciliation_status: ReconciliationStatus;
  reconciliation_note?: string | null | undefined;
  total_inflows: number;
  total_outflows: number;
  expected_balance: number;
  actual_balance: number;
  difference: number;
  verified_by?: string | null | undefined;
  verified_by_name?: string | null | undefined;
  verified_user_type?: EntityTypes;
}

export interface ShiftPage {
  shifts: Shift[];
  count: number;
  cursor?: string | null | undefined;
}

export interface CreateShift {
  pos_id?: string | null | undefined;
  opening_balances: PaymentBalance[];
  opening_note?: string | null | undefined;
  merchant_id: string;
  store_id: string;
  opened_by: string;
  opened_by_name: string;
  opened_user_type: EntityTypes;
}

export interface PaymentBalance {
  payment_method_id: string;
  payment_method_name: string;
  payment_method_type: PaymentMethodType;
  balance: number;
  note?: string | null | undefined;
}

export interface AttemptToCloseShift {
  closing_balances: PaymentBalance[];
  closing_note?: string | null | undefined;
  closed_by: string;
  closed_by_name: string;
  closed_user_type: EntityTypes;
}

export interface CloseShiftWithReconciliation {
  reconciliations: PaymentBalance[];
  note?: string | null | undefined;
  verified_by: string;
  verified_by_name: string;
  verified_user_type: EntityTypes;
  force_close: boolean;
}

export interface InstantCloseShift {
  closing_balances: PaymentBalance[];
  closed_by: string;
  closed_by_name: string;
  closed_user_type: EntityTypes;
  reason: string;
}

export interface AddShiftComment {
  shift_id: string;
  user_id: string;
  user_name: string;
  user_type: EntityTypes;
  comment: string;
  merchant_id: string;
  store_id: string;
}

export interface ShiftDbItem extends Shift {
  PK: string; // STORE#<store_id>
  SK: string; // SHIFT#<shift_id>
  GSI1PK: string; // <opened_user_type>#<opened_by>
  GSI1SK: string; // SHIFT#<opened_at>
  GSI2PK?: string; // POS_DEVICE#<pos_id>
  GSI2SK?: string; // SHIFT#<opened_at>
  GSI3PK: string; // SHIFT_STATUS#<status>
  GSI3SK: string; // SHIFT#<opened_at>
  EntityIndexPK: string; // SHIFT
  EntityIndexSK: string; // MERCHANT#<merchant_id>#STORE#<store_id>#DATE#<created_at>
}

export interface GetShifts {
  store_id: string;
  merchant_id: string;
  cursor?: string | null | undefined;
  limit?: number;
  status?: ShiftStatus;
  opened_by?: string | null | undefined;
  opened_user_type?: EntityTypes;
  pos_id?: string | null | undefined;
  from?: string | null | undefined;
  to?: string | null | undefined;
  by_date_range?: boolean;
  by_status?: boolean;
  by_status_and_date_range?: boolean;
  by_user?: boolean;
  by_user_and_date_range?: boolean;
  by_pos?: boolean;
  by_pos_and_date_range?: boolean;
  by_store?: boolean;
  by_store_and_date_range?: boolean;
}

/**
 * PDF shift-report stored in S3 and listed in the Reports ? Downloads page.
 * Generated server-side either on demand or automatically when a shift moves
 * to a terminal status (CLOSED / CLOSED_WITH_VARIANCE).
 */
export type ShiftReportTrigger = 'manual' | 'auto-on-close';

export interface ShiftReportRecord {
  /** S3 object key. */
  key: string;
  /** Human-friendly file name (yymmddhhmm-shift-report-{store}-{employee}.pdf). */
  file_name: string;
  shift_id: string;
  merchant_id: string;
  store_id: string;
  store_name: string;
  employee_name: string;
  shift_status: ShiftStatus;
  /** ISO timestamp the shift was closed. */
  closed_at: string;
  /** ISO timestamp the report was generated. */
  requested_at: string;
  triggered_by: ShiftReportTrigger;
  download_url: string;
  requested_by?: string | null | undefined;
  size_in_bytes?: number;
  last_modified?: string;
}

export interface ShiftReportsResponse {
  reports: ShiftReportRecord[];
  next_cursor?: string;
}

export interface GenerateShiftReportRequest {
  /** Force regeneration even if an existing report is found in S3. */
  force?: boolean;
}

export interface GenerateShiftReportResponse {
  record: ShiftReportRecord;
  /** True when the report was rendered and uploaded; false when an existing object was reused. */
  created: boolean;
}

/**
 * Detail of the EventBridge shift.closed event emitted by service-shifts
 * after a shift transitions to a terminal status. Consumed by service-reports
 * to auto-generate the PDF shift report.
 */
export enum ShiftEvents {
  SHIFT_CLOSED = 'shift.closed',
}

export interface ShiftClosedEventDetail {
  shift_id: string;
  merchant_id: string;
  store_id: string;
  status: ShiftStatus;
  closed_at: string;
  closed_by?: string | null | undefined;
  closed_by_name?: string | null | undefined;
}
